// @ts-nocheck
import useEditing, { CloseEditToast } from "components/EditDrawer/useEditing";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import "./RouterPrompt.css";
import { t } from "i18next";
import useCurPage from "hooks/useCurPage";

const pathOf = (to) =>
	typeof to === "string"
		? to
		: (to?.pathname || "/") + (to?.search || "") + (to?.hash || "");

/**
 * Navigation prompt when user has unsaved changes (v1 behaviour: Save /
 * Draft / Cancel before leaving).
 *
 * v1 used `history.block`. We run on <BrowserRouter> (no data router, so no
 * `useBlocker`): in-app navigations (links, navigate()) are intercepted on the
 * router's navigator, and the browser back/forward buttons are reverted then
 * replayed once the user confirms. Closing/reloading the tab keeps the native
 * `beforeunload` warning.
 */
export function RouterPrompt(props) {
	const { when, onOK, onCancel, onDraft, title, okText, cancelText } = props;
	const { setEditing } = useEditing();
	const { navigator } = useContext(UNSAFE_NavigationContext);
	const { setCurPage } = useCurPage();
	const [showPrompt, setShowPrompt] = useState(false);
	const [currentPath, setCurrentPath] = useState("");
	const unblockRef = useRef(() => {});

	useEffect(() => {
		if (!when) return;
		const handler = (e) => {
			e.preventDefault();
			e.returnValue = "";
		};
		window.addEventListener("beforeunload", handler);
		return () => window.removeEventListener("beforeunload", handler);
	}, [when]);

	useEffect(() => {
		if (!when) {
			setShowPrompt(false);
			return;
		}
		const { push, replace } = navigator;
		const intercept = (to) => {
			setCurrentPath(pathOf(to));
			setShowPrompt(true);
		};
		navigator.push = (to) => intercept(to);
		navigator.replace = (to) => intercept(to);

		// Back / forward: put the history back where it was, ask, then replay.
		let idx = window.history.state?.idx ?? 0;
		let reverting = false;
		const onPop = (e) => {
			if (reverting) {
				reverting = false;
				return;
			}
			const next = e.state?.idx ?? idx;
			const target =
				window.location.pathname + window.location.search + window.location.hash;
			if (next !== idx) {
				reverting = true;
				window.history.go(idx - next);
			}
			setCurrentPath(target);
			setShowPrompt(true);
		};
		window.addEventListener("popstate", onPop);

		const unblock = () => {
			navigator.push = push;
			navigator.replace = replace;
			window.removeEventListener("popstate", onPop);
		};
		unblockRef.current = unblock;
		return unblock;
	}, [navigator, when]);

	const handleOK = useCallback(async () => {
		if (onOK) {
			const canRoute = await Promise.resolve(onOK());
			if (canRoute) {
				unblockRef.current();
				setCurPage(currentPath, true);
				setEditing(false);
				setShowPrompt(false);
			}
		}
	}, [currentPath, onOK, setCurPage, setEditing]);

	const handleCancel = useCallback(async () => {
		if (onCancel) {
			const canRoute = await Promise.resolve(onCancel());
			if (canRoute) {
				unblockRef.current();
				setCurPage(currentPath, true);
			}
		}
		setShowPrompt(false);
	}, [currentPath, onCancel, setCurPage]);

	const handleDraft = useCallback(async () => {
		if (onDraft) {
			const canRoute = await Promise.resolve(onDraft());
			if (canRoute) {
				unblockRef.current();
				setCurPage(currentPath, true);
			}
		}
		setShowPrompt(false);
	}, [currentPath, onDraft, setCurPage]);

	return showPrompt ? (
		<div className="RouterPrompt">
			<CloseEditToast
				title={title}
				visible={showPrompt}
				onOk={handleOK}
				okText={okText}
				onDraft={onDraft ? handleDraft : false}
				onCancel={handleCancel}
				cancelText={cancelText}
			>
				<FontAwesomeIcon
					icon={faExclamationTriangle}
					style={{ fontSize: "2rem" }}
					className="mb-2"
					color={"orange"}
				/>
				{t("Editing.UNSAVED_WARN")}
			</CloseEditToast>
		</div>
	) : null;
}
